// Firebase Proxy Setup for China Users
// Intercepts Firebase and Google services API calls to route through proxy server

/**
 * Setup Firebase proxy interception for China users
 * @param {Object} config - Configuration object
 * @param {string} config.chinaHostnamePattern - Pattern to detect China users (e.g., 'china-alcoholometry')
 * @param {string} config.proxyHost - Proxy hostname (e.g., 'china-alcoholometry.labox-apps.com')
 * @param {string} config.defaultBaseUrl - Default base URL
 * @param {string} config.chinaBaseUrl - China base URL
 */
const setupFirebaseProxyInternal = (config) => {
  const {
    chinaHostnamePattern,
    proxyHost,
    chinaBaseUrl,
  } = config;

  // Check if already set up to avoid duplicate initialization
  // eslint-disable-next-line dot-notation
  if (window['__firebaseProxySetup']) {
    return;
  }
  // eslint-disable-next-line dot-notation
  window['__firebaseProxySetup'] = true;

  /**
   * Check if the current user is accessing from China
   * @returns {boolean} True if user is accessing via China proxy URL
   */
  const isChinaUser = () => window.location.hostname.includes(chinaHostnamePattern);

  if (!isChinaUser()) {
    // eslint-disable-next-line no-console
    console.log('[Firebase Proxy] Not a China user, skipping proxy setup');
    return;
  }

  // eslint-disable-next-line no-console
  console.log('[Firebase Proxy] Setting up proxy interception for China user');

  /**
   * Intercept and rewrite URLs for Google services blocked in China
   * @param {string} urlString - Original URL
   * @returns {string} Modified URL if it should be proxied, original URL otherwise
   */
  const interceptGoogleServices = (urlString) => {
    if (typeof urlString !== 'string') return urlString;

    // Intercept Firebase Auth action URLs (email verification, password reset, etc.)
    // These URLs are generated by Firebase Auth with the authDomain, so we need to rewrite them
    if (urlString.includes('orgs-ws.labox-apps.com/__/auth/action')) {
      return urlString.replace('orgs-ws.labox-apps.com', proxyHost);
    }

    // Match patterns for services blocked in China
    const patterns = [
      /https:\/\/([^/]+\.googleapis\.com)/, // Firebase APIs
      /https:\/\/([^/]+\.googletagmanager\.com)/, // Google Tag Manager
      /https:\/\/([^/]+\.google-analytics\.com)/, // Google Analytics (region1, etc.)
      /https:\/\/([^/]+\.cloudfunctions\.net)/, // Cloud Functions
      /https:\/\/([^/]+\.sentry\.io)/, // Sentry (may be blocked in China)
    ];

    for (const pattern of patterns) {
      const match = urlString.match(pattern);
      if (match) {
        const originalDomain = match[1];
        let newUrl = urlString.replace(`https://${originalDomain}`, `https://${proxyHost}`);

        // Special handling for Cloud Functions: rewrite path to match nginx pattern
        if (originalDomain.includes('.cloudfunctions.net')) {
          // Support:
          // - region-project.cloudfunctions.net (e.g., europe-west1-labox-ws)
          // - region-zone-project.cloudfunctions.net (rare, but keep compatibility)
          let region;

          // Pattern 1: region-zone-project.cloudfunctions.net (e.g., europe-west1-labox-ws)
          let regionMatch = originalDomain.match(/([^-]+)-([^-]+)-([^-]+)-([^.]+)\.cloudfunctions\.net/);
          if (regionMatch) {
            const [, regionPart1, regionPart2] = regionMatch;
            region = `${regionPart1}-${regionPart2}`;
          } else {
            // Pattern 2: region-project.cloudfunctions.net (e.g., europe-west1-labox-ws)
            regionMatch = originalDomain.match(/([^-]+)-([^-]+)-([^.]+)\.cloudfunctions\.net/);
            if (regionMatch) {
              const [, regionPart1, regionPart2] = regionMatch;
              region = `${regionPart1}-${regionPart2}`;
            }
          }

          if (region) {
            // Extract function name from full path
            try {
              const urlPath = new URL(urlString).pathname;
              const functionName = urlPath.split('/').filter((p) => p).pop() || '';
              newUrl = `https://${proxyHost}/labox-ws/${region}/${functionName}`;
              // Preserve query string if present
              const queryString = urlString.includes('?') ? urlString.substring(urlString.indexOf('?')) : '';
              if (queryString) {
                newUrl += queryString;
              }
            } catch (e) {
              // eslint-disable-next-line no-console
              console.warn('[Firebase Proxy] Failed to parse Cloud Functions URL:', urlString, e);
            }
          } else {
            // eslint-disable-next-line no-console
            console.warn('[Firebase Proxy] Cloud Functions domain pattern mismatch:', originalDomain);
          }
        }

        // eslint-disable-next-line no-console
        console.log(`[Firebase Proxy] Intercepted: ${urlString} -> ${newUrl}`);
        return newUrl;
      }
    }
    return urlString;
  };

  // Intercept fetch
  // Note: Extending native fetch is necessary to intercept Firebase API calls
  // eslint-disable-next-line no-extend-native
  const originalFetch = window.fetch;
  // eslint-disable-next-line no-extend-native
  window.fetch = function proxiedFetch(...args) {
    const [url, ...restArgs] = args;
    let modifiedUrl = url;

    // Handle Request object
    if (url instanceof Request) {
      const requestUrl = url.url;
      const newUrl = interceptGoogleServices(requestUrl);
      if (newUrl !== requestUrl) {
        modifiedUrl = new Request(newUrl, url);
      }
    } else if (url instanceof URL) {
      const requestUrl = url.toString();
      const newUrl = interceptGoogleServices(requestUrl);
      // fetch accepts URL objects, but after rewrite we must pass a string
      if (newUrl !== requestUrl) {
        modifiedUrl = newUrl;
      }
    } else if (typeof modifiedUrl === 'string') {
      modifiedUrl = interceptGoogleServices(modifiedUrl);
    }
    return originalFetch.call(this, modifiedUrl, ...restArgs);
  };

  // Intercept XMLHttpRequest
  // Note: Extending native XMLHttpRequest is necessary to intercept Firebase API calls
  // eslint-disable-next-line no-extend-native
  const originalOpen = XMLHttpRequest.prototype.open;
  // eslint-disable-next-line no-extend-native
  XMLHttpRequest.prototype.open = function proxiedOpen(method, url, ...rest) {
    const modifiedUrl = interceptGoogleServices(url);
    return originalOpen.call(this, method, modifiedUrl, ...rest);
  };

  // Intercept script and img tag creation to proxy Google services
  // This is needed for Google Tag Manager and Analytics which load via <script> tags
  // Only intercept setAttribute for URLs that match Google services patterns
  const originalCreateElement = document.createElement;
  // eslint-disable-next-line no-extend-native
  document.createElement = function proxiedCreateElement(tagName, ...rest) {
    const element = originalCreateElement.call(this, tagName, ...rest);
    const tagLower = tagName.toLowerCase();
    if (tagLower === 'script' || tagLower === 'img' || tagLower === 'iframe') {
      const originalSetAttribute = element.setAttribute;
      element.setAttribute = function proxiedSetAttribute(name, value, ...restArgs) {
        if ((name === 'src' || name === 'href') && typeof value === 'string') {
          const modifiedValue = interceptGoogleServices(value);
          // Only use modified value if it was actually changed (i.e., it matched a Google service)
          if (modifiedValue !== value) {
            return originalSetAttribute.call(this, name, modifiedValue, ...restArgs);
          }
        }
        return originalSetAttribute.call(this, name, value, ...restArgs);
      };
      // Don't intercept src property assignment to avoid breaking React and other libraries
      // setAttribute interception is sufficient for most cases
    }
    return element;
  };

  // Also intercept DOM mutations to catch dynamically added scripts/images/iframes
  const originalAppendChild = Node.prototype.appendChild;
  // eslint-disable-next-line no-extend-native
  Node.prototype.appendChild = function proxiedAppendChild(child, ...rest) {
    if (child.tagName === 'SCRIPT' || child.tagName === 'IMG' || child.tagName === 'IFRAME') {
      const src = child.getAttribute('src') || child.src;
      if (src && typeof src === 'string') {
        const modifiedSrc = interceptGoogleServices(src);
        if (modifiedSrc !== src) {
          child.setAttribute('src', modifiedSrc);
        }
      }
    }
    return originalAppendChild.call(this, child, ...rest);
  };

  // Intercept navigator.sendBeacon (used by Analytics for tracking)
  const originalSendBeacon = navigator.sendBeacon;
  // eslint-disable-next-line no-extend-native
  navigator.sendBeacon = function proxiedSendBeacon(url, data) {
    const urlString = typeof url === 'string' ? url : url.toString();
    const modifiedUrl = interceptGoogleServices(urlString);
    return originalSendBeacon.call(this, modifiedUrl, data);
  };

  // Export isChinaUser and getEmailVerificationUrl functions
  // Store them on window for access from other modules
  // eslint-disable-next-line dot-notation
  window['__firebaseProxyIsChinaUser'] = isChinaUser;
  // eslint-disable-next-line dot-notation
  window['__firebaseProxyGetEmailVerificationUrl'] = (path = '/account?confirm_email=true') => {
    if (isChinaUser()) {
      return `${chinaBaseUrl}${path}`;
    }
    return `${window.location.protocol}//${window.location.host}${path}`;
  };
};

/**
 * Check if the current user is accessing from China
 * Must be called after setupFirebaseProxy
 * @returns {boolean} True if user is accessing via China proxy URL
 */
export const isChinaUser = () => {
  // eslint-disable-next-line dot-notation
  if (window['__firebaseProxyIsChinaUser']) {
    // eslint-disable-next-line dot-notation
    return window['__firebaseProxyIsChinaUser']();
  }
  return false;
};

/**
 * Get the application URL based on app ID and user context (China or not)
 * @param {string} appId - Application ID (e.g., 'alcoholometry', 'digitank-tanker-trucks')
 * @returns {string} Full URL to the application
 */
export const getAppUrl = (appId) => {
  const isChina = isChinaUser();

  // Map of app IDs to their domains
  const appDomains = {
    alcoholometry: isChina ? 'china-alcoholometry.labox-apps.com' : 'alcoholometry.labox-apps.com',
    'digitank-tanker-trucks': 'tt.digitank.labox-apps.com', // No China version
    'digitank-gauged-containers': 'gc.digitank.labox-apps.com', // No China version
    'digitank-transporter': 'tt.digitank.labox-apps.com', // No China version
    'digitank-cleaning-certificates': 'cc.digitank.labox-apps.com', // No China version
    'digitank-gauger': 'gauger.labox-apps.com', // No China version
    inventory: 'inventory.labox-apps.com', // No China version
  };

  const domain = appDomains[appId];
  if (!domain) {
    return '';
  }

  return `https://${domain}`;
};

/**
 * Get the email verification redirect URL based on user context
 * Must be called after setupFirebaseProxy
 * @param {string} path - Path to redirect to (default: '/account?confirm_email=true')
 * @returns {string} Full URL for email verification redirect
 */
export const getEmailVerificationUrl = (path = '/account?confirm_email=true') => {
  // For organizations-ws, always return the organizations-ws URL
  // Check if we're on a China domain (any china-* domain)
  const isChinaDomain = window.location.hostname.includes('china-');
  if (isChinaDomain) {
    // Return china-orgs-ws URL for organizations-ws
    return `https://china-orgs-ws.labox-apps.com${path}`;
  }
  // For non-China domains, return orgs-ws URL
  return `https://orgs-ws.labox-apps.com${path}`;
};

// Initialize proxy with Organizations-WS-specific configuration
setupFirebaseProxyInternal({
  chinaHostnamePattern: 'china-orgs-ws',
  proxyHost: 'china-orgs-ws.labox-apps.com',
  defaultBaseUrl: 'https://orgs-ws.labox-apps.com',
  chinaBaseUrl: 'https://china-orgs-ws.labox-apps.com',
});

// Export setupFirebaseProxy for compatibility
export const setupFirebaseProxy = setupFirebaseProxyInternal;
